/**
 * Divides the input array into fixed sized arrays or "chunks".
 * @template T
 * @param {number} chunkSize - the max size of each chunk
 * @param {T[]} arr - the input array
 * @returns {T[][]}
 */
export const chunk = <T>(chunkSize: number, arr: T[]): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    result.push(arr.slice(i, i + chunkSize));
  }
  return result;
};

/**
 * Divides a list of strings into chunks whose JSON-encoded size stays within
 * `maxBytes`. A value larger than the budget still gets its own chunk.
 * @param {number} maxBytes - the max JSON-encoded size of each chunk
 * @param {string[]} values - the input array
 * @returns {string[][]}
 */
export const chunkBySerializedSize = (
  maxBytes: number,
  values: string[],
): string[][] => {
  const encoder = new TextEncoder();
  const result: string[][] = [];
  let current: string[] = [];
  let currentBytes = 0;

  values.forEach((value) => {
    // +3 covers the surrounding quotes and the separating comma
    const valueBytes = encoder.encode(value).length + 3;
    if (current.length > 0 && currentBytes + valueBytes > maxBytes) {
      result.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(value);
    currentBytes += valueBytes;
  });

  if (current.length > 0) {
    result.push(current);
  }
  return result;
};

export const sum = (arr: number[]) => arr.reduce((acc, cur) => acc + cur, 0);

export const avg = (arr: number[]) => sum(arr) / arr.length;

export const min = (arr: number[]) => Math.min(...arr);

export const max = (arr: number[]) => Math.max(...arr);
